import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from './i18n';
import './BlacklistManager.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlacklistManager = ({ onClose }) => {
  const [blacklistedUsers, setBlacklistedUsers] = useState([]);
  const [newUserId, setNewUserId] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { language } = useLanguage();

  const translations = {
    kk: {
      title: 'Қара тізім басқару',
      addUser: 'Пайдаланушыны қосу',
      userId: 'Пайдаланушы ID',
      reason: 'Себебі',
      add: 'Қосу',
      remove: 'Жою',
      noUsers: 'Қара тізімде пайдаланушылар жоқ',
      confirmRemove: 'Осы пайдаланушыны қара тізімнен шығару керек пе?',
      reasonRequired: 'Себебін көрсету қажет',
      userIdRequired: 'Пайдаланушы ID көрсету қажет',
      added: 'Пайдаланушы қара тізімге қосылды',
      removed: 'Пайдаланушы қара тізімнен шығарылды',
      close: 'Жабу',
      loading: 'Жүктеу...',
      error: 'Қате орын алды'
    },
    ru: {
      title: 'Управление черным списком',
      addUser: 'Добавить пользователя',
      userId: 'ID пользователя',
      reason: 'Причина',
      add: 'Добавить',
      remove: 'Удалить',
      noUsers: 'В черном списке нет пользователей',
      confirmRemove: 'Удалить этого пользователя из черного списка?',
      reasonRequired: 'Необходимо указать причину',
      userIdRequired: 'Необходимо указать ID пользователя',
      added: 'Пользователь добавлен в черный список',
      removed: 'Пользователь удален из черного списка',
      close: 'Закрыть',
      loading: 'Загрузка...',
      error: 'Произошла ошибка'
    }
  };

  const t = translations[language];

  useEffect(() => {
    loadBlacklistedUsers();
  }, []);

  const loadBlacklistedUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/blacklist`);
      setBlacklistedUsers(response.data || []);
    } catch (err) {
      console.error('Error loading blacklisted users:', err);
      setError(err.response?.data?.detail || t.error);
    } finally {
      setLoading(false);
    }
  };

  const addToBlacklist = async () => {
    if (!newUserId.trim()) {
      alert(t.userIdRequired);
      return;
    }
    if (!reason.trim()) {
      alert(t.reasonRequired);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/blacklist`, {
        user_id: newUserId.trim(),
        reason: reason.trim()
      });
      
      alert(t.added);
      setNewUserId('');
      setReason('');
      loadBlacklistedUsers();
    } catch (err) {
      console.error('Error adding to blacklist:', err);
      alert(err.response?.data?.detail || t.error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromBlacklist = async (userId) => {
    if (!window.confirm(t.confirmRemove)) {
      return;
    }

    try {
      await axios.delete(`${API}/blacklist/${userId}`);
      
      alert(t.removed);
      loadBlacklistedUsers();
    } catch (err) {
      console.error('Error removing from blacklist:', err);
      alert(err.response?.data?.detail || t.error);
    }
  };

  return (
    <div className="blacklist-panel">
      <div className="blacklist-header">
        <h2>{t.title}</h2>
        <button className="close-button" onClick={onClose}>×</button>
      </div>

      <div className="blacklist-content">
        {error && (
          <div className="error-message">{error}</div>
        )}
        
        <div className="add-user-section">
          <h3>{t.addUser}</h3>
          <div className="input-group">
            <input
              type="text"
              placeholder={t.userId}
              value={newUserId}
              onChange={(e) => setNewUserId(e.target.value)}
            />
            <textarea
              placeholder={t.reason}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows="3"
            />
            <button 
              onClick={addToBlacklist}
              disabled={loading}
              className="add-button"
            >
              {loading ? '...' : t.add}
            </button>
          </div>
        </div>

        <div className="users-list">
          {loading ? (
            <p className="loading-text">{t.loading}</p>
          ) : blacklistedUsers.length === 0 ? (
            <p className="no-users">{t.noUsers}</p>
          ) : (
            blacklistedUsers.map(user => (
              <div key={user.id || user.user_id} className="blacklist-user">
                <div className="user-info">
                  <strong>{user.user_name || user.user_id}</strong>
                  <p>{user.reason}</p>
                  <small>{user.created_at ? new Date(user.created_at).toLocaleDateString() : ''}</small>
                </div>
                <button 
                  onClick={() => removeFromBlacklist(user.user_id)}
                  className="remove-button"
                >
                  {t.remove}
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="blacklist-footer">
        <button onClick={onClose} className="close-footer-button">
          {t.close}
        </button>
      </div>
    </div>
  );
};

export default BlacklistManager;
